import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, Upload, ExternalLink, Star, 
  Image as ImageIcon, Film, Quote, User, X, GripVertical, Music, Globe, Video,
  Monitor, Tablet, Smartphone
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Helper function to fix image URLs from old domains or relative paths
const fixImageUrl = (url) => {
  if (!url) return null;
  
  // Handle relative paths starting with /api/uploads/
  if (url.startsWith('/api/uploads/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  
  // Handle URLs containing /api/uploads/ from old domains
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/[^\s"']+/);
    if (match) {
      return `${process.env.REACT_APP_BACKEND_URL}${match[0]}`;
    }
  }
  
  // Handle relative paths
  if (url.startsWith('/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  
  // Handle absolute URLs - return as is
  if (url.startsWith('http')) return url;
  
  // Handle other cases
  return `${process.env.REACT_APP_BACKEND_URL}/${url}`;
};

const CMSPortfolioPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [uploading, setUploading] = useState(false);
  const [repairingImages, setRepairingImages] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { 
      const res = await api.get('/cms/portfolio'); 
      setProjects(res.data); 
    }
    catch (error) { toast.error('Erreur de chargement'); } 
    finally { setLoading(false); }
  };

  // Repair external image URLs
  const handleRepairImages = async () => {
    setRepairingImages(true);
    try {
      // First, preview what will be localized
      const previewRes = await api.get('/cms/localize-images/preview');
      const totalImages = previewRes.data?.total_external_images || 0;
      
      if (totalImages === 0) {
        toast.success('✅ Toutes les images sont déjà localisées !');
        setRepairingImages(false);
        return;
      }
      
      toast.info(`🔄 Localisation de ${totalImages} images en cours...`);
      
      // Run the localization
      const res = await api.post('/cms/localize-images');
      const { images_found, images_localized, images_failed } = res.data;
      
      if (images_localized > 0) {
        toast.success(`✅ ${images_localized}/${images_found} images localisées avec succès !`);
        loadData(); // Reload to show updated images
      }
      
      if (images_failed > 0) {
        toast.warning(`⚠️ ${images_failed} images n'ont pas pu être téléchargées`);
      }
    } catch (error) {
      console.error('Error repairing images:', error);
      toast.error('Erreur lors de la réparation des images');
    } finally {
      setRepairingImages(false);
    }
  };

  // Repair broken slugs
  const handleRepairSlugs = async () => {
    try {
      toast.info('🔄 Réparation des URLs en cours...');
      const res = await api.post('/cms/fix-slugs');
      const { fixed, already_ok, details } = res.data;
      
      if (fixed > 0) {
        toast.success(`✅ ${fixed} URLs corrigées !`);
        loadData();
        // Show details
        details.slice(0, 3).forEach(d => {
          toast.info(`${d.title}: ${d.old_slug} → ${d.new_slug}`);
        });
      } else {
        toast.success('✅ Toutes les URLs sont déjà correctes !');
      }
    } catch (error) {
      console.error('Error repairing slugs:', error);
      toast.error('Erreur lors de la réparation des URLs');
    }
  };

  // Check and fix broken screenshots
  const handleCheckScreenshots = async () => {
    try {
      toast.info('🔄 Vérification des captures d\'écran...');
      const res = await api.post('/cms/check-screenshots');
      const { checked, missing, existing, cleaned } = res.data;
      
      if (missing.length === 0) {
        toast.success(`✅ Toutes les ${existing} captures sont valides !`);
      } else {
        toast.warning(`⚠️ ${missing.length} captures manquantes sur ${checked}`);
        if (cleaned > 0) {
          toast.info(`🧹 ${cleaned} références cassées supprimées`);
          loadData();
        }
        // Show which projects need screenshots
        const projects = [...new Set(missing.map(m => m.project))];
        toast.info(`Projets à ré-uploader: ${projects.slice(0, 3).join(', ')}${projects.length > 3 ? '...' : ''}`);
      }
    } catch (error) {
      console.error('Error checking screenshots:', error);
      toast.error('Erreur lors de la vérification');
    }
  };

  const handleSave = async () => {
    const { data } = editModal;
    try {
      if (data.id) { 
        await api.put(`/cms/portfolio/${data.id}`, data); 
      } else { 
        await api.post('/cms/portfolio', data); 
      }
      toast.success('Projet enregistré'); 
      loadData(); 
      setEditModal({ open: false, data: null });
    } catch (error) { 
      toast.error('Erreur lors de l\'enregistrement'); 
    }
  };

  const handleDelete = async () => {
    try { 
      await api.delete(`/cms/portfolio/${deleteModal.id}`); 
      toast.success('Projet supprimé'); 
      loadData(); 
      setDeleteModal({ open: false, id: null }); 
    }
    catch (error) { toast.error('Erreur'); }
  };

  const handleUpload = async (e, field = 'image') => {
    const file = e.target.files?.[0]; 
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData(); 
    formData.append('file', file); 
    formData.append('folder', 'portfolio');
    
    try { 
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }); 
      
      if (field === 'image') {
        setEditModal({...editModal, data: {...editModal.data, image: res.data.url}}); 
      } else if (field === 'client_logo') {
        setEditModal({...editModal, data: {...editModal.data, client_logo_url: res.data.url}});
      } else if (field === 'testimonial_photo') {
        setEditModal({
          ...editModal, 
          data: {
            ...editModal.data, 
            testimonial: {...(editModal.data.testimonial || {}), author_photo: res.data.url}
          }
        });
      }
      toast.success('Fichier uploadé'); 
    }
    catch (error) { 
      console.error('Upload error:', error);
      toast.error('Erreur d\'upload'); 
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    const newItems = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'portfolio');

      try {
        const res = await api.post('/cms/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newItems.push({
          type: file.type.startsWith('video') ? 'video' : 'image',
          url: res.data.url,
          caption: ''
        });
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    const currentGallery = editModal.data?.gallery || [];
    setEditModal({
      ...editModal,
      data: {
        ...editModal.data,
        gallery: [...currentGallery, ...newItems]
      }
    });
    setUploading(false);
    if (newItems.length > 0) toast.success(`${newItems.length} fichier(s) ajouté(s)`);
  };

  const removeGalleryItem = (index) => {
    const gallery = [...(editModal.data?.gallery || [])];
    gallery.splice(index, 1);
    setEditModal({...editModal, data: {...editModal.data, gallery}});
  };

  // Helper function to extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    // Support various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/  // Direct video ID
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  const addYouTubeToGallery = (url) => {
    if (!url) {
      toast.error('Veuillez entrer une URL YouTube');
      return;
    }
    
    const videoId = getYouTubeId(url);
    if (!videoId) {
      toast.error('URL YouTube invalide. Formats acceptés: youtube.com/watch?v=XXX, youtu.be/XXX');
      return;
    }
    
    // Store normalized URL
    const normalizedUrl = `https://www.youtube.com/embed/${videoId}`;
    const gallery = editModal.data?.gallery || [];
    setEditModal({
      ...editModal,
      data: {
        ...editModal.data,
        gallery: [...gallery, { type: 'youtube', url: normalizedUrl, videoId, caption: '' }]
      }
    });
    toast.success('Vidéo YouTube ajoutée');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Réalisations</h1>
          <p className="text-slate-500">Gérez votre portfolio de projets avec galeries et témoignages</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline"
            onClick={handleCheckScreenshots}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Monitor className="w-4 h-4 mr-2" />
            Vérifier captures
          </Button>
          <Button 
            variant="outline"
            onClick={handleRepairSlugs}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Globe className="w-4 h-4 mr-2" />
            Réparer URLs
          </Button>
          <Button 
            variant="outline"
            onClick={handleRepairImages}
            disabled={repairingImages}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {repairingImages ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ImageIcon className="w-4 h-4 mr-2" />
            )}
            {repairingImages ? 'Réparation...' : 'Réparer images'}
          </Button>
          <Button 
            onClick={() => setEditModal({ 
              open: true, 
              data: { 
                is_active: true, 
                tags: [], 
                results: [], 
                gallery: [],
                testimonial_enabled: false,
                testimonial: { text: '', author_name: '', author_role: '', rating: 5 },
                order: projects.length + 1 
              } 
            })} 
            className="bg-gradient-to-r from-cyan-500 to-blue-600"
          >
            <Plus className="w-4 h-4 mr-2" />Nouveau projet
          </Button>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {projects.map(project => (
          <Card key={project.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="h-40 bg-slate-100 relative flex items-center justify-center">
              {project.image ? (
                <img 
                  src={fixImageUrl(project.image)} 
                  alt={project.title} 
                  className="w-full h-full object-cover" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect fill="%23f1f5f9" width="400" height="300"/><text x="50%" y="50%" font-family="Arial" font-size="14" fill="%2394a3b8" text-anchor="middle">Image non disponible</text></svg>';
                  }}
                />
              ) : (
                <div className="text-center text-slate-400">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                  <span className="text-sm">Pas d&apos;image</span>
                </div>
              )}
              {project.is_featured && (
                <Badge className="absolute top-2 right-2 bg-amber-500">
                  <Star className="w-3 h-3 mr-1" />Mis en avant
                </Badge>
              )}
              {project.gallery?.length > 0 && (
                <Badge className="absolute top-2 left-2 bg-purple-500">
                  <ImageIcon className="w-3 h-3 mr-1" />{project.gallery.length}
                </Badge>
              )}
              {project.testimonial_enabled && (
                <Badge className="absolute bottom-2 left-2 bg-green-500">
                  <Quote className="w-3 h-3 mr-1" />Témoignage
                </Badge>
              )}
            </div>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <Badge variant="outline">{project.category}</Badge>
                {project.client_logo_url && (
                  <img 
                    src={project.client_logo_url.startsWith('http') ? project.client_logo_url : `${process.env.REACT_APP_BACKEND_URL}${project.client_logo_url}`}
                    alt={project.client}
                    className="h-6 w-auto object-contain"
                  />
                )}
              </div>
              <h3 className="font-semibold text-lg">{project.title}</h3>
              <p className="text-sm text-slate-500">{project.client}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {(project.tags || []).slice(0, 3).map((tag, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>
              <div className="mt-4 flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, data: project })}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, id: project.id })}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Modal with Tabs */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, data: null })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editModal.data?.id ? 'Modifier' : 'Nouveau'} projet</DialogTitle>
          </DialogHeader>
          
          {editModal.data && (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">Général</TabsTrigger>
                <TabsTrigger value="gallery">Galerie</TabsTrigger>
                <TabsTrigger value="testimonial">Témoignage</TabsTrigger>
              </TabsList>

              {/* General Tab */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input 
                      value={editModal.data.title || ''} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, title: e.target.value}})} 
                      placeholder="Nom du projet"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Input 
                      value={editModal.data.client || ''} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, client: e.target.value}})} 
                      placeholder="Nom du client"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Logo du client</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={editModal.data.client_logo_url || ''} 
                        onChange={(e) => setEditModal({...editModal, data: {...editModal.data, client_logo_url: e.target.value}})} 
                        placeholder="URL du logo"
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        disabled={uploading}
                        onClick={() => document.getElementById('logo-upload-input').click()}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                      <input 
                        id="logo-upload-input"
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={(e) => handleUpload(e, 'client_logo')} 
                      />
                    </div>
                    {editModal.data.client_logo_url && (
                      <img 
                        src={editModal.data.client_logo_url.startsWith('http') ? editModal.data.client_logo_url : `${process.env.REACT_APP_BACKEND_URL}${editModal.data.client_logo_url}`} 
                        alt="Logo client" 
                        className="h-12 object-contain bg-slate-50 rounded p-2" 
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select 
                      value={editModal.data.category || ''} 
                      onValueChange={(v) => setEditModal({...editModal, data: {...editModal.data, category: v}})}
                    >
                      <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="graphique">Création Graphique</SelectItem>
                        <SelectItem value="web">Site Web</SelectItem>
                        <SelectItem value="video">Vidéo</SelectItem>
                        <SelectItem value="audio">Audio / Podcast</SelectItem>
                        <SelectItem value="marketing">Marketing Digital</SelectItem>
                        <SelectItem value="branding">Branding</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={editModal.data.description || ''} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, description: e.target.value}})} 
                    placeholder="Description courte du projet"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Image principale</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={editModal.data.image || ''} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, image: e.target.value}})} 
                      className="flex-1" 
                      placeholder="URL ou upload" 
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      disabled={uploading}
                      onClick={() => document.getElementById('main-image-upload-input').click()}
                    >
                      {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </Button>
                    <input 
                      id="main-image-upload-input"
                      type="file" 
                      className="hidden" 
                      accept="image/*" 
                      onChange={(e) => handleUpload(e, 'image')} 
                    />
                  </div>
                  {editModal.data.image && (
                    <img 
                      src={fixImageUrl(editModal.data.image)} 
                      alt="Preview" 
                      className="h-32 object-contain mx-auto bg-slate-50 rounded p-2" 
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>URL du site web</Label>
                    <Input 
                      value={editModal.data.website_url || ''} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, website_url: e.target.value}})} 
                      placeholder="https://..."
                    />
                    <p className="text-xs text-slate-500">Pour les réalisations web : lien vers le site en ligne</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Slug URL</Label>
                    <Input 
                      value={editModal.data.slug || ''} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, slug: e.target.value}})} 
                      placeholder="mon-projet"
                    />
                  </div>
                </div>

                {/* Web-specific fields - Responsive Screenshots */}
                {editModal.data.category === 'web' && (
                  <div className="space-y-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-cyan-600" />
                      <Label className="font-semibold text-cyan-900">Captures d'écran responsive</Label>
                    </div>
                    <p className="text-sm text-cyan-700">
                      Uploadez des captures d'écran longues de la page pour un effet de scroll automatique
                    </p>
                    <div className="grid gap-4 md:grid-cols-3">
                      {/* Desktop Screenshot */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Monitor className="w-4 h-4" /> Desktop
                        </Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            id="screenshot-desktop"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('device', 'desktop');
                                try {
                                  const res = await api.post('/cms/upload-screenshot', formData);
                                  setEditModal({...editModal, data: {...editModal.data, screenshot_desktop: res.data.url}});
                                  toast.success('Image desktop uploadée');
                                } catch (err) {
                                  toast.error('Erreur upload');
                                }
                              }
                            }}
                          />
                          <label
                            htmlFor="screenshot-desktop"
                            className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:bg-cyan-100 transition-colors"
                          >
                            <Upload className="w-4 h-4 text-cyan-600" />
                            <span className="text-sm text-cyan-700">Choisir PNG</span>
                          </label>
                        </div>
                        {editModal.data.screenshot_desktop && (
                          <div className="relative group">
                            <img 
                              src={editModal.data.screenshot_desktop.startsWith('http') ? editModal.data.screenshot_desktop : `${process.env.REACT_APP_BACKEND_URL}${editModal.data.screenshot_desktop}`} 
                              alt="Desktop preview" 
                              className="w-full h-24 object-cover object-top rounded border" 
                            />
                            <button
                              type="button"
                              onClick={() => setEditModal({...editModal, data: {...editModal.data, screenshot_desktop: ''}})}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Tablet Screenshot */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Tablet className="w-4 h-4" /> Tablette
                        </Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            id="screenshot-tablet"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('device', 'tablet');
                                try {
                                  const res = await api.post('/cms/upload-screenshot', formData);
                                  setEditModal({...editModal, data: {...editModal.data, screenshot_tablet: res.data.url}});
                                  toast.success('Image tablette uploadée');
                                } catch (err) {
                                  toast.error('Erreur upload');
                                }
                              }
                            }}
                          />
                          <label
                            htmlFor="screenshot-tablet"
                            className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:bg-cyan-100 transition-colors"
                          >
                            <Upload className="w-4 h-4 text-cyan-600" />
                            <span className="text-sm text-cyan-700">Choisir PNG</span>
                          </label>
                        </div>
                        {editModal.data.screenshot_tablet && (
                          <div className="relative group">
                            <img 
                              src={editModal.data.screenshot_tablet.startsWith('http') ? editModal.data.screenshot_tablet : `${process.env.REACT_APP_BACKEND_URL}${editModal.data.screenshot_tablet}`} 
                              alt="Tablet preview" 
                              className="w-full h-24 object-cover object-top rounded border" 
                            />
                            <button
                              type="button"
                              onClick={() => setEditModal({...editModal, data: {...editModal.data, screenshot_tablet: ''}})}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Mobile Screenshot */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Smartphone className="w-4 h-4" /> Mobile
                        </Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            id="screenshot-mobile"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('device', 'mobile');
                                try {
                                  const res = await api.post('/cms/upload-screenshot', formData);
                                  setEditModal({...editModal, data: {...editModal.data, screenshot_mobile: res.data.url}});
                                  toast.success('Image mobile uploadée');
                                } catch (err) {
                                  toast.error('Erreur upload');
                                }
                              }
                            }}
                          />
                          <label
                            htmlFor="screenshot-mobile"
                            className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-cyan-300 rounded-lg cursor-pointer hover:bg-cyan-100 transition-colors"
                          >
                            <Upload className="w-4 h-4 text-cyan-600" />
                            <span className="text-sm text-cyan-700">Choisir PNG</span>
                          </label>
                        </div>
                        {editModal.data.screenshot_mobile && (
                          <div className="relative group">
                            <img 
                              src={editModal.data.screenshot_mobile.startsWith('http') ? editModal.data.screenshot_mobile : `${process.env.REACT_APP_BACKEND_URL}${editModal.data.screenshot_mobile}`} 
                              alt="Mobile preview" 
                              className="w-full h-24 object-cover object-top rounded border" 
                            />
                            <button
                              type="button"
                              onClick={() => setEditModal({...editModal, data: {...editModal.data, screenshot_mobile: ''}})}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Audio-specific fields */}
                {editModal.data.category === 'audio' && (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Music className="w-5 h-5 text-blue-500" />
                      <Label className="font-semibold text-blue-900">Options Audio</Label>
                    </div>
                    <div className="space-y-2">
                      <Label>URL du fichier audio</Label>
                      <Input 
                        value={editModal.data.audio_url || ''} 
                        onChange={(e) => setEditModal({...editModal, data: {...editModal.data, audio_url: e.target.value}})} 
                        placeholder="https://... ou /api/uploads/audio/fichier.mp3"
                      />
                      <p className="text-xs text-slate-500">Formats supportés: MP3, WAV, OGG</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Titre de la piste</Label>
                      <Input 
                        value={editModal.data.audio_title || ''} 
                        onChange={(e) => setEditModal({...editModal, data: {...editModal.data, audio_title: e.target.value}})} 
                        placeholder="Nom de la piste audio"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Tags (un par ligne)</Label>
                  <Textarea 
                    value={(editModal.data.tags || []).join('\n')} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, tags: e.target.value.split('\n').filter(t => t.trim())}})} 
                    rows={2} 
                    placeholder="Branding&#10;Logo&#10;Digital"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Résultats clés (un par ligne)</Label>
                  <Textarea 
                    value={(editModal.data.results || []).join('\n')} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, results: e.target.value.split('\n').filter(r => r.trim())}})} 
                    rows={2} 
                    placeholder="+50% de visibilité&#10;3x plus de leads"
                  />
                </div>

                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Switch 
                    checked={editModal.data.is_featured || false} 
                    onCheckedChange={(checked) => setEditModal({...editModal, data: {...editModal.data, is_featured: checked}})} 
                  />
                  <div>
                    <Label className="font-medium">Projet mis en avant</Label>
                    <p className="text-xs text-slate-500">Apparaîtra en premier dans la liste</p>
                  </div>
                </div>
              </TabsContent>

              {/* Gallery Tab */}
              <TabsContent value="gallery" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-purple-500" />
                      Galerie d'images et vidéos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Upload Section */}
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        disabled={uploading}
                        onClick={() => document.getElementById('gallery-upload-input').click()}
                      >
                        {uploading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        Ajouter des fichiers
                      </Button>
                      <input 
                        id="gallery-upload-input"
                        type="file" 
                        className="hidden" 
                        accept="image/*,video/*" 
                        multiple
                        onChange={handleGalleryUpload} 
                      />
                      <div className="flex gap-2 flex-1">
                        <Input 
                          placeholder="URL YouTube..." 
                          id="youtube-url"
                          className="flex-1"
                        />
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => {
                            const input = document.getElementById('youtube-url');
                            addYouTubeToGallery(input.value);
                            input.value = '';
                          }}
                        >
                          <Film className="h-4 w-4 mr-2" />
                          Ajouter YouTube
                        </Button>
                      </div>
                    </div>

                    {/* Gallery Grid */}
                    {editModal.data.gallery?.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {editModal.data.gallery.map((item, idx) => (
                          <div key={idx} className="relative group rounded-lg overflow-hidden bg-slate-100 aspect-video">
                            {item.type === 'image' ? (
                              <img 
                                src={item.url.startsWith('http') ? item.url : `${process.env.REACT_APP_BACKEND_URL}${item.url}`} 
                                alt={`Galerie ${idx + 1}`} 
                                className="w-full h-full object-cover"
                              />
                            ) : item.type === 'youtube' ? (
                              <div className="w-full h-full flex items-center justify-center bg-red-100">
                                <Film className="w-8 h-8 text-red-500" />
                              </div>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-blue-100">
                                <Film className="w-8 h-8 text-blue-500" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => removeGalleryItem(idx)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <Badge className="absolute bottom-1 left-1 text-xs">
                              {item.type === 'youtube' ? 'YouTube' : item.type}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                        <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p>Aucun média dans la galerie</p>
                        <p className="text-sm">Ajoutez des images ou vidéos pour enrichir votre projet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Testimonial Tab */}
              <TabsContent value="testimonial" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Quote className="w-5 h-5 text-green-500" />
                      Témoignage client
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Enable Toggle */}
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <Switch 
                        checked={editModal.data.testimonial_enabled || false} 
                        onCheckedChange={(checked) => setEditModal({
                          ...editModal, 
                          data: {...editModal.data, testimonial_enabled: checked}
                        })} 
                      />
                      <div>
                        <Label className="font-medium">Afficher le témoignage</Label>
                        <p className="text-xs text-slate-500">Le témoignage sera visible sur la page du projet</p>
                      </div>
                    </div>

                    {/* Testimonial Fields */}
                    <div className={cn(!editModal.data.testimonial_enabled && "opacity-50 pointer-events-none")}>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Texte du témoignage</Label>
                          <Textarea 
                            value={editModal.data.testimonial?.text || ''} 
                            onChange={(e) => setEditModal({
                              ...editModal, 
                              data: {
                                ...editModal.data, 
                                testimonial: {...(editModal.data.testimonial || {}), text: e.target.value}
                              }
                            })} 
                            rows={4}
                            placeholder="Ce que le client a dit sur le projet..."
                          />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Nom de l'auteur</Label>
                            <Input 
                              value={editModal.data.testimonial?.author_name || ''} 
                              onChange={(e) => setEditModal({
                                ...editModal, 
                                data: {
                                  ...editModal.data, 
                                  testimonial: {...(editModal.data.testimonial || {}), author_name: e.target.value}
                                }
                              })} 
                              placeholder="Jean Dupont"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Rôle / Fonction</Label>
                            <Input 
                              value={editModal.data.testimonial?.author_role || ''} 
                              onChange={(e) => setEditModal({
                                ...editModal, 
                                data: {
                                  ...editModal.data, 
                                  testimonial: {...(editModal.data.testimonial || {}), author_role: e.target.value}
                                }
                              })} 
                              placeholder="Directeur Marketing"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Photo de l'auteur</Label>
                          <div className="flex gap-2 items-center">
                            <Input 
                              value={editModal.data.testimonial?.author_photo || ''} 
                              onChange={(e) => setEditModal({
                                ...editModal, 
                                data: {
                                  ...editModal.data, 
                                  testimonial: {...(editModal.data.testimonial || {}), author_photo: e.target.value}
                                }
                              })} 
                              placeholder="URL de la photo"
                              className="flex-1"
                            />
                            <label className="cursor-pointer">
                              <input type="file" className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'testimonial_photo')} />
                              <Button type="button" variant="outline" disabled={uploading}>
                                <Upload className="h-4 w-4" />
                              </Button>
                            </label>
                          </div>
                          {editModal.data.testimonial?.author_photo && (
                            <img 
                              src={editModal.data.testimonial.author_photo} 
                              alt="Auteur" 
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Vidéo témoignage (YouTube)</Label>
                          <Input 
                            value={editModal.data.testimonial?.video_url || ''} 
                            onChange={(e) => setEditModal({
                              ...editModal, 
                              data: {
                                ...editModal.data, 
                                testimonial: {...(editModal.data.testimonial || {}), video_url: e.target.value}
                              }
                            })} 
                            placeholder="https://youtube.com/watch?v=..."
                          />
                          <p className="text-xs text-slate-500">
                            Si une vidéo est fournie, elle sera affichée à la place du texte
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Note ({editModal.data.testimonial?.rating || 5}/5)</Label>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setEditModal({
                                  ...editModal, 
                                  data: {
                                    ...editModal.data, 
                                    testimonial: {...(editModal.data.testimonial || {}), rating: star}
                                  }
                                })}
                                className="focus:outline-none"
                              >
                                <Star 
                                  className={cn(
                                    "w-6 h-6 transition-colors",
                                    star <= (editModal.data.testimonial?.rating || 5) 
                                      ? "fill-yellow-400 text-yellow-400" 
                                      : "text-slate-300"
                                  )} 
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, data: null })}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              <Save className="w-4 h-4 mr-2" />Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer ce projet ?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500">Cette action est irréversible. Le projet sera définitivement supprimé.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, id: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSPortfolioPage;
